import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import SearchService from '~/services/SearchService';

import { filterLePlaceIds } from '~/utils/lePlaceId';

import { type NameLookupEntry, buildNameLookupMap } from '../buildNameLookupMap';
import { useNormalizedIds } from '../useNormalizedIds';

import { searchPlaceQueryKeys } from './searchPlaceQueryKeys';

export type PlaceGeom = {
  placeId: string;
  name: string;
  type: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  geom: { type: string; coordinates: any } | null;
};

export type PlaceNameLookup = NameLookupEntry & {
  type: string | null;
};

export function useGetPlaces(placeIds: Array<string>, options?: { requireGeom?: boolean }) {
  const requireGeom = options?.requireGeom ?? false;
  const candidateIds = useNormalizedIds(placeIds);
  const normalizedIds = useMemo(() => filterLePlaceIds(candidateIds), [candidateIds]);

  const { data, isFetching, isError } = useQuery({
    queryKey: searchPlaceQueryKeys.list(normalizedIds, requireGeom),
    queryFn: async ({ signal }): Promise<Array<PlaceGeom>> => {
      const fetchedPlaces = (await SearchService.getPlacesByIds(normalizedIds, { signal })).result;

      const placesById = new Map<string, PlaceGeom>();

      fetchedPlaces.forEach((place) => {
        const placeWithName = place as typeof place & { name?: string; nameFull?: string };
        const name = placeWithName.name ?? placeWithName.nameFull ?? place.placeId;
        placesById.set(place.placeId, {
          placeId: place.placeId,
          name,
          type: place.type,
          geom: place.geom ?? null,
        });
      });

      return normalizedIds.map((id) => placesById.get(id)).filter((place): place is PlaceGeom => Boolean(place));
    },
    enabled: normalizedIds.length > 0,
    retry: false,
  });

  const places = data ?? [];

  const placeNamesById = useMemo(() => {
    const placesById = new Map(places.map((place) => [place.placeId, place]));

    return buildNameLookupMap(
      normalizedIds,
      (id) => {
        const place = placesById.get(id);
        return {
          name: place?.name ?? null,
          type: place?.type ?? null,
        };
      },
      { isLoading: isFetching, isError },
    );
  }, [normalizedIds, places, isFetching, isError]);

  return { places, placeNamesById, isFetching, isError };
}
